import {
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from '@tanstack/react-router';
import { X } from 'lucide-react';
import type { GlobalSuggestHit } from '@/gql/graphql';
import { Text } from '@/components/ui/Text';
import { useGlobalSearch } from '@/lib/search/useGlobalSearch';
import { useGlobalSearchTelemetry } from '@/lib/search/useGlobalSearchTelemetry';
import { SearchHitRow } from './SearchHitRow';
import { GlobalSearchEmptyState } from './GlobalSearchEmptyState';
import { GlobalSearchUnavailable } from './GlobalSearchUnavailable';
import { cn } from '@/lib/cn';
import { SearchInput } from '@/components/ui/SearchInput';

/** §6.7 — query is preserved for 60 seconds across Esc / ⌘K toggles. */
const QUERY_PRESERVATION_MS = 60_000;

/** A60 Q-A5 / §6.5 — show empty-state message only after this grace period. */
const NO_RESULTS_GRACE_MS = 800;

export interface GlobalSearchPaletteProps {
  isOpen: boolean;
  onClose: () => void;
  /**
   * Called when the user selects a hit.
   * Wired to navigation + telemetry in T03; palette itself stays navigation-agnostic.
   */
  onSelect?: (hit: GlobalSuggestHit, rank: number) => void;
  /** Initial query (restored externally e.g. from Navbar state). */
  initialQuery?: string;
}

/**
 * GlobalSearchPalette — the search command palette for ENT-60.
 *
 * Renders as a fixed overlay portal.  State machine:
 *   CLOSED → OPEN_EMPTY → OPEN_FETCHING → OPEN_RESULTS | OPEN_EMPTY_STATE | OPEN_UNAVAILABLE
 *
 * Design §6 / handover §2.
 */
export function GlobalSearchPalette({
  isOpen,
  onClose,
  onSelect,
  initialQuery = '',
}: GlobalSearchPaletteProps) {
  const [query, setQuery] = useState(initialQuery);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const [showEmptyState, setShowEmptyState] = useState(false);

  // Query preservation (§6.7)
  const lastQueryRef = useRef('');
  const queryExpiresAtRef = useRef(0);

  // Row element refs for auto-scroll on keyboard navigation
  const rowRefsRef = useRef<(HTMLDivElement | null)[]>([]);

  const inputRef = useRef<HTMLInputElement>(null);

  const navigate = useNavigate();
  const { emitClick, emitDismissed } = useGlobalSearchTelemetry();

  const { hits, isFetching, isFirstFetch, partialFailure } = useGlobalSearch(
    query,
    isOpen,
  );

  // Clamp highlightedIndex to valid range — eliminates the need to reset via effect
  // when hits shrink. When hits = [], this resolves to -1 (nothing highlighted).
  const effectiveHighlightedIndex =
    hits.length === 0 ? -1 : Math.min(highlightedIndex, hits.length - 1);

  // ── Open / close lifecycle ────────────────────────────────────────────

  useEffect(() => {
    if (isOpen) {
      // Restore preserved query if still within window
      const restored =
        Date.now() < queryExpiresAtRef.current ? lastQueryRef.current : initialQuery;
      // Batch state resets + focus via rAF so they run outside the effect body
      // (avoids react-hooks/set-state-in-effect rule while keeping correct timing)
      const raf = requestAnimationFrame(() => {
        setQuery(restored);
        setShowEmptyState(false);
        inputRef.current?.focus();
      });
      return () => cancelAnimationFrame(raf);
    }
  }, [isOpen, initialQuery]);

  const handleClose = useCallback(() => {
    // Preserve query for 60s (§6.7)
    lastQueryRef.current = query;
    queryExpiresAtRef.current = Date.now() + QUERY_PRESERVATION_MS;
    // Emit dismissed telemetry (only on close without selection — A60.12)
    emitDismissed({ query, totalHitsShown: hits.length });
    onClose();
  }, [query, onClose, hits.length, emitDismissed]);

  // ── No-results grace period ───────────────────────────────────────────

  useEffect(() => {
    if (hits.length === 0 && !partialFailure && query.length >= 2 && !isFetching) {
      const timer = setTimeout(() => setShowEmptyState(true), NO_RESULTS_GRACE_MS);
      return () => clearTimeout(timer);
    }
    // Defer reset to avoid synchronous setState in effect body
    const raf = requestAnimationFrame(() => setShowEmptyState(false));
    return () => cancelAnimationFrame(raf);
  }, [hits.length, partialFailure, query, isFetching]);

  // ── Auto-scroll highlighted row into view ─────────────────────────────

  useEffect(() => {
    if (effectiveHighlightedIndex >= 0) {
      rowRefsRef.current[effectiveHighlightedIndex]?.scrollIntoView({ block: 'nearest' });
    }
  }, [effectiveHighlightedIndex]);

  // ── Hit selection (defined before handleKeyDown — used in Enter branch) ──

  const handleSelect = useCallback(
    (hit: GlobalSuggestHit, rank: number) => {
      // Emit click telemetry (A60.12) — 1-indexed rank within the flat list
      emitClick({ kind: hit.kind, rank, query });
      // External handler (Navbar etc.) — optional
      onSelect?.(hit, rank);
      // Navigate to server-computed route (may contain hash e.g. /entities/<id>#appointments).
      // `route` is nullable in the schema; skip navigation when absent rather than routing to "".
      if (hit.route) {
        if (hit.search) {
          const params = new URLSearchParams(hit.search as Record<string, string>).toString();
          navigate({ to: `${hit.route}?${params}` });
        } else {
          navigate({ to: hit.route });
        }
      }
      // Close palette directly — bypass handleClose to avoid emitting dismissed telemetry
      onClose();
    },
    [emitClick, query, onSelect, navigate, onClose],
  );

  // ── Keyboard navigation ───────────────────────────────────────────────

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLDivElement>) => {
      if (e.key === 'Escape') {
        handleClose();
        return;
      }

      if (hits.length === 0) return;

      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setHighlightedIndex((prev) => (prev + 1) % hits.length);
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        setHighlightedIndex((prev) => (prev <= 0 ? hits.length - 1 : prev - 1));
      } else if (e.key === 'Enter' && effectiveHighlightedIndex >= 0) {
        e.preventDefault();
        const hit = hits[effectiveHighlightedIndex];
        if (hit) {
          handleSelect(hit, effectiveHighlightedIndex + 1);
        }
      }
    },
    [hits, effectiveHighlightedIndex, handleClose, handleSelect],
  );

  // ── Click-outside to close ────────────────────────────────────────────

  const backdropRef = useRef<HTMLDivElement>(null);

  const handleBackdropClick = useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      if (e.target === backdropRef.current) {
        handleClose();
      }
    },
    [handleClose],
  );

  // ── Derived state flags ───────────────────────────────────────────────

  const showList = hits.length > 0;
  const showUnavailable = partialFailure && hits.length === 0;
  const showHints = query.length === 0;

  if (!isOpen) return null;

  return createPortal(
    <div
      ref={backdropRef}
      className="fixed inset-0 z-50 flex items-start justify-center pt-16 px-4 bg-black/20 backdrop-blur-[1px]"
      onClick={handleBackdropClick}
      aria-modal="true"
      role="dialog"
      aria-label="Global search"
    >
      <div
        className="w-full max-w-lg bg-surface-elevated rounded-lg shadow-2xl border border-border overflow-hidden"
        onKeyDown={handleKeyDown}
      >
        {/* ── Search input header ─────────────────────────────────── */}
        <div className="flex items-center gap-2.5 px-3 py-2.5 border-b border-border">
          <SearchInput
            variant="flush"
            ref={inputRef}
            value={query}
            onValueChange={setQuery}
            placeholder="Search entities, people, obligations…"
            aria-label="Search"
            aria-autocomplete="list"
            aria-controls="global-search-listbox"
            aria-activedescendant={
              effectiveHighlightedIndex >= 0 ? `search-hit-${effectiveHighlightedIndex}` : undefined
            }
            role="combobox"
            aria-expanded={showList}
            autoComplete="off"
            spellCheck={false}
          />
          {query && (
            <button
              type="button"
              onClick={() => setQuery('')}
              className="text-text-muted hover:text-text transition-colors rounded p-0.5"
              aria-label="Clear search"
            >
              <X size={14} aria-hidden />
            </button>
          )}
          <kbd
            className={cn(
              'hidden sm:inline-flex items-center px-1.5 py-0.5 rounded',
              'bg-surface-muted border border-border text-text-muted font-mono font-mono-face text-label-sm',
            )}
          >
            ⌘K
          </kbd>
        </div>

        {/* ── Freshness hint (empty query only — A60.14 / D8) ──────── */}
        {showHints && (
          <Text
            variant="body-sm"
            color="muted"
            className="block px-3 pt-2 pb-3"
          >
            Recently created records may take a few seconds to appear.
          </Text>
        )}

        {/* ── 4px loading progress bar (first fetch only) ──────────── */}
        {isFirstFetch && (
          <div
            className="h-1 bg-primary animate-pulse"
            role="progressbar"
            aria-label="Loading search results"
          />
        )}

        {/* ── Results list ─────────────────────────────────────────── */}
        {showList && (
          <div
            id="global-search-listbox"
            role="listbox"
            aria-label="Search results"
            className="max-h-[480px] overflow-y-auto py-1"
          >
            {hits.map((hit, index) => (
              <SearchHitRow
                key={hit.id}
                hit={hit}
                isHighlighted={index === effectiveHighlightedIndex}
                rank={index + 1}
                onSelect={handleSelect}
                rowRef={(el) => {
                  rowRefsRef.current[index] = el;
                }}
              />
            ))}
          </div>
        )}

        {/* ── No-results state (800ms grace — A60 Q-A5 / §6.5) ────── */}
        {showEmptyState && !showList && !showUnavailable && (
          <GlobalSearchEmptyState query={query} />
        )}

        {/* ── Total-failure banner (§6.6) ───────────────────────────── */}
        {showUnavailable && <GlobalSearchUnavailable />}
      </div>
    </div>,
    document.body,
  );
}
